function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function smsNode(
  id: string,
  label: string,
  y: number,
  to: string,
  message: string,
  x = 80,
): any {
  return n(id, "sendSMS", label, y, { sendSmsDetails: { to, message } }, x);
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}

export const ELITE_BATCH22_PART12: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ─── 139. Form Submission to Owner Assignment (Pattern K) ─────────────

  "Form Submission to Owner Assignment": {
    nodes: [
      trigger("HubSpot webhook: New form submission received", "webhook_app", {
        source: "hubspot",
        event: "form.submission.created",
        howItStarts:
          "Fires when a prospect submits any tracked HubSpot form (demo request, contact us, gated content). The webhook payload contains the form ID, submitted fields, and page URL.",
        setup:
          "In HubSpot, go to Settings > Integrations > Webhooks and subscribe to the form.submission.created event. Map the webhook URL to this workflow trigger.",
        inputFields: [],
      }),
      n(
        "2",
        "hitApi",
        "HubSpot: Enrich contact with company and lifecycle data",
        280,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.hubapi.com/crm/v3/contacts/{contact_id}?properties=email,firstname,lastname,company,jobtitle,lifecyclestage,hs_lead_status,country,annualrevenue",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              {
                name: "properties.email",
                customName: "contact_email",
                type: "string",
              },
              {
                name: "properties.firstname",
                customName: "first_name",
                type: "string",
              },
              {
                name: "properties.lastname",
                customName: "last_name",
                type: "string",
              },
              {
                name: "properties.company",
                customName: "company_name",
                type: "string",
              },
              {
                name: "properties.jobtitle",
                customName: "job_title",
                type: "string",
              },
              {
                name: "properties.country",
                customName: "country",
                type: "string",
              },
              {
                name: "properties.annualrevenue",
                customName: "annual_revenue",
                type: "string",
              },
              {
                name: "properties.lifecyclestage",
                customName: "lifecycle_stage",
                type: "string",
              },
            ],
          },
        },
      ),
      n(
        "3",
        "runCode",
        "Compute lead score and determine territory owner",
        520,
        {
          runCodeDetails: {
            code: `const revenue = parseInt(input.annual_revenue) || 0;
const title = (input.job_title || "").toLowerCase();
const country = (input.country || "").toLowerCase();
let fit_score = 0;
if (revenue > 1000000) fit_score += 40; else if (revenue > 100000) fit_score += 20;
if (title.includes("vp") || title.includes("director") || title.includes("cto") || title.includes("ceo")) fit_score += 30; else if (title.includes("manager")) fit_score += 15;
fit_score += 10;
const territory = (country === "us" || country === "united states") ? "NA" : (country === "uk" || country === "germany" || country === "france") ? "EMEA" : "APAC";
const owners = { NA: "ae-north-america@company.com", EMEA: "ae-emea@company.com", APAC: "ae-apac@company.com" };
const owner_email = owners[territory] || owners.NA;
return { fit_score, territory, owner_email, is_high_priority: fit_score >= 50 };`,
          },
        },
      ),
      n("4", "condition", "Lead fit score >= 50 (high priority)?", 760, {
        conditionDetails: {
          conditions: [
            { field: "fit_score", operator: "greaterThanOrEqual", value: "50" },
          ],
        },
      }),
      slackNode(
        "5",
        "Slack: Urgent alert — high-priority lead assigned to territory owner",
        1000,
        "🔥 *High-Priority Form Submission*\n*Contact:* {first_name} {last_name} ({contact_email})\n*Company:* {company_name} | *Title:* {job_title}\n*Revenue:* ${annual_revenue} | *Territory:* {territory}\n*Fit Score:* {fit_score}/100\n*Owner:* {owner_email}\nAction required within 1 hour.",
      ),
      n("6", "delayAction", "Wait 2 hours for SLA compliance check", 1240, {
        delayDetails: { duration: 2, unit: "hours" },
      }),
      n("7", "hitApi", "HubSpot: Update contact owner and lead status", 1480, {
        hitApiDetails: {
          method: "PATCH",
          url: "https://api.hubapi.com/crm/v3/contacts/{contact_id}",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "properties",
              value:
                '{"hubspot_owner_id":"{owner_email}","hs_lead_status":"NEW","lead_score":"{fit_score}"}',
            },
          ],
          variables: [
            { name: "id", customName: "updated_contact_id", type: "string" },
          ],
        },
      }),
      sheetsNode(
        "8",
        "Google Sheets: Log form routing decision and SLA status",
        1720,
        "Form_Routing_Log",
        '[["{contact_email}","{company_name}","{fit_score}","{territory}","{owner_email}","{is_high_priority}","{new Date().toISOString()}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "6", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Routes new HubSpot form submissions to the correct sales owner based on territory and lead fit score. Enriches the contact profile, computes a fit score from revenue and job title, sends an urgent Slack alert for high-priority leads (score >= 50), waits for SLA compliance, updates the HubSpot contact with the assigned owner and lead status, and logs the routing decision to Google Sheets for conversion quality tracking.",
  },

  // ─── 140. Abandoned Cart Recovery (Pattern B) ────────────────────────

  "Abandoned Cart Recovery": {
    nodes: [
      trigger(
        "Shopify webhook: Cart abandoned in HubSpot-tracked store",
        "webhook_app",
        {
          source: "shopify",
          event: "checkouts/create",
          howItStarts:
            "Fires when a Shopify checkout is created but not completed within the abandonment window. The webhook contains the checkout token, line items, total price, and customer email if available.",
          setup:
            "In Shopify, go to Settings > Notifications > Webhooks and add checkouts/create. Ensure HubSpot-Shopify integration syncs customer data so abandoned carts are linked to CRM contacts.",
          inputFields: [],
        },
      ),
      aiNode(
        "2",
        "OpenAI: Extract cart details and craft personalized reminder angles",
        280,
        "You are an e-commerce recovery specialist. Given raw abandoned cart data, extract: customer_email, customer_name, cart_items (array with name, price, image_url), cart_total, currency, checkout_url, highest_value_item, and generate a personalized_subject_line and urgency_angle for the recovery email.",
        "Analyze this abandoned cart payload and extract structured details. Cart data: {webhook_payload}. Generate a personalized recovery email subject line and urgency angle based on the items and cart value.",
        [
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "cart_items", as: "cart_items", type: "object" },
          { field: "cart_total", as: "cart_total", type: "string" },
          { field: "checkout_url", as: "checkout_url", type: "string" },
          {
            field: "highest_value_item",
            as: "highest_value_item",
            type: "string",
          },
          {
            field: "personalized_subject_line",
            as: "personalized_subject_line",
            type: "string",
          },
          { field: "urgency_angle", as: "urgency_angle", type: "string" },
        ],
      ),
      n(
        "3",
        "hitApi",
        "HubSpot: Lookup contact purchase history and cart value tier",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.hubapi.com/crm/v3/contacts/{contact_email}?idProperty=email&properties=email,firstname,total_revenue,num_associated_deals,hs_buying_role",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              {
                name: "properties.firstname",
                customName: "hubspot_first_name",
                type: "string",
              },
              {
                name: "properties.total_revenue",
                customName: "total_revenue",
                type: "string",
              },
              {
                name: "properties.num_associated_deals",
                customName: "past_orders",
                type: "string",
              },
            ],
          },
        },
      ),
      n("4", "condition", "Cart value > $100?", 760, {
        conditionDetails: {
          conditions: [
            { field: "cart_total", operator: "greaterThan", value: "100" },
          ],
        },
      }),
      emailNode(
        "5",
        "Email: First cart recovery — personalized product reminder",
        1000,
        "{customer_email}",
        "{personalized_subject_line}",
        "Hi {customer_name},\n\nYou left some great items in your cart — including {highest_value_item}.\n\nYour cart total: ${cart_total}\n\n{urgency_angle}\n\nComplete your purchase now: {checkout_url}\n\nNeed help? Reply to this email and we'll assist you right away.\n\nBest,\nThe Team",
      ),
      n("6", "delayAction", "Wait 12 hours before second reminder", 1240, {
        delayDetails: { duration: 12, unit: "hours" },
      }),
      n("7", "condition", "Customer still has not completed purchase?", 1480, {
        conditionDetails: {
          conditions: [
            { field: "past_orders", operator: "equals", value: "0" },
          ],
        },
      }),
      emailNode(
        "8",
        "Email: Second reminder — social proof and free shipping incentive",
        1720,
        "{customer_email}",
        "Still thinking about {highest_value_item}? Here's a nudge",
        "Hi {customer_name},\n\nHundreds of customers love {highest_value_item} — it's one of our best sellers.\n\n⭐ 4.8/5 average rating from 2,000+ reviews\n\nAs a thank you for coming back, enjoy FREE SHIPPING on your order.\n\nComplete your purchase: {checkout_url}\n\nYour cart (${cart_total}) is saved for a limited time.\n\nCheers,\nThe Team",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
    summary:
      "Recovers abandoned Shopify carts through a two-touch HubSpot email sequence. Extracts cart details via AI including personalized subject lines and urgency angles, enriches the contact through HubSpot CRM, segments by cart value (over/under $100), sends a first personalized reminder with product details, waits 12 hours, checks if the purchase is still incomplete, and sends a second email featuring social proof and a free shipping incentive.",
  },

  // ─── 141. Post-Purchase Review Request (Pattern E) ───────────────────

  "Post-Purchase Review Request": {
    nodes: [
      trigger("Shopify webhook: Order fulfilled and delivered", "webhook_app", {
        source: "shopify",
        event: "orders/fulfilled",
        howItStarts:
          "Fires when a Shopify order is marked as fulfilled/delivered. The webhook payload contains the order ID, line items, customer email, shipping address, and fulfillment tracking details.",
        setup:
          "In Shopify, subscribe to the orders/fulfilled webhook event. Ensure HubSpot integration syncs order data so customer profiles reflect purchase history.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "OpenAI: Extract order details and generate review request messaging",
        280,
        "You are a post-purchase experience specialist. Extract: customer_email, customer_name, order_id, product_names (array), primary_product (the highest-value item), order_total, delivery_date, and generate a friendly review_request_subject and review_cta_text tailored to the product category.",
        "Parse this fulfilled order payload and extract structured details. Order data: {webhook_payload}. Generate a warm, non-pushy review request subject line and CTA text appropriate for the product category.",
        [
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "order_id", as: "order_id", type: "string" },
          { field: "primary_product", as: "primary_product", type: "string" },
          { field: "order_total", as: "order_total", type: "string" },
          {
            field: "review_request_subject",
            as: "review_request_subject",
            type: "string",
          },
          { field: "review_cta_text", as: "review_cta_text", type: "string" },
        ],
      ),
      n("3", "hitApi", "HubSpot: Lookup customer tenure and VIP status", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.hubapi.com/crm/v3/contacts/{customer_email}?idProperty=email&properties=email,firstname,num_associated_deals,hs_object_id,lifecyclestage,hs_lead_status",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "properties.num_associated_deals",
              customName: "total_orders",
              type: "string",
            },
            {
              name: "properties.lifecyclestage",
              customName: "lifecycle_stage",
              type: "string",
            },
            { name: "id", customName: "hubspot_contact_id", type: "string" },
          ],
        },
      }),
      n("4", "condition", "Is this their first purchase?", 760, {
        conditionDetails: {
          conditions: [
            { field: "total_orders", operator: "lessThanOrEqual", value: "1" },
          ],
        },
      }),
      emailNode(
        "5",
        "Email: Thank you with product care tips for new customers",
        1000,
        "{customer_email}",
        "Welcome to the family! Here's how to get the most from {primary_product}",
        "Hi {customer_name},\n\nThank you for your first order (#{order_id})! We're thrilled to have you.\n\nHere are a few tips to get the most out of your {primary_product}:\n\n1. Unbox carefully and inspect all components\n2. Follow the quick-start guide included in your package\n3. Register your product for warranty coverage\n\nWe'd love to hear how it goes — stay tuned for a quick follow-up in a few days.\n\nWelcome aboard!\nThe Team",
      ),
      n(
        "6",
        "delayAction",
        "Wait 7 days for customer to use the product",
        1240,
        {
          delayDetails: { duration: 7, unit: "days" },
        },
      ),
      n(
        "7",
        "condition",
        "Order was delivered (fulfillment confirmed)?",
        1480,
        {
          conditionDetails: {
            conditions: [
              {
                field: "lifecycle_stage",
                operator: "notEquals",
                value: "churned",
              },
            ],
          },
        },
      ),
      emailNode(
        "8",
        "Email: Review request with direct link and small incentive",
        1720,
        "{customer_email}",
        "{review_request_subject}",
        "Hi {customer_name},\n\nYou've had your {primary_product} for about a week now — how's it going?\n\n{review_cta_text}\n\nLeave a review here: https://shop.example.com/reviews/{order_id}\n\nAs a thank you, you'll get 10% off your next order when you submit a review.\n\nYour feedback helps other shoppers and helps us improve.\n\nThanks!\nThe Team",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "6", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
    summary:
      "Requests product reviews from customers after order fulfillment with a two-phase approach. Extracts order details via AI and generates personalized review messaging, checks HubSpot for customer tenure and VIP status, sends a thank-you email with product care tips for first-time buyers, waits 7 days for product usage, confirms the customer is still active, and sends a review request email with a 10% incentive for submitting feedback.",
  },

  // ─── 142. Customer Onboarding Workflow (Pattern E) ───────────────────

  "Customer Onboarding Workflow": {
    nodes: [
      trigger(
        "HubSpot webhook: Deal closed-won — new customer created",
        "webhook_app",
        {
          source: "hubspot",
          event: "deal.propertyChange",
          howItStarts:
            "Fires when a HubSpot deal moves to the closed-won stage, indicating a new customer. The webhook contains the deal ID, associated contact and company IDs, deal amount, and pipeline stage details.",
          setup:
            "In HubSpot, create a workflow trigger on deal stage change to 'closedwon'. Subscribe to the deal.propertyChange webhook event filtered to dealstage = closedwon.",
          inputFields: [],
        },
      ),
      aiNode(
        "2",
        "OpenAI: Extract deal details and build onboarding plan",
        280,
        "You are a customer success onboarding planner. Extract: contact_email, contact_name, company_name, deal_amount, product_purchased, and generate an onboarding_plan with 3-5 steps tailored to the product, a welcome_subject line, and an estimated onboarding_duration_days.",
        "Parse this closed-won deal and build a tailored onboarding plan. Deal data: {webhook_payload}. Create specific onboarding steps based on the product purchased and deal size.",
        [
          { field: "contact_email", as: "contact_email", type: "string" },
          { field: "contact_name", as: "contact_name", type: "string" },
          { field: "company_name", as: "company_name", type: "string" },
          { field: "deal_amount", as: "deal_amount", type: "string" },
          {
            field: "product_purchased",
            as: "product_purchased",
            type: "string",
          },
          { field: "onboarding_plan", as: "onboarding_plan", type: "object" },
          { field: "welcome_subject", as: "welcome_subject", type: "string" },
        ],
      ),
      n(
        "3",
        "hitApi",
        "HubSpot: Fetch associated contact profile and company details",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.hubapi.com/crm/v3/contacts/{contact_email}?idProperty=email&properties=email,firstname,lastname,company,jobtitle,phone,lifecyclestage",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              {
                name: "properties.firstname",
                customName: "first_name",
                type: "string",
              },
              {
                name: "properties.phone",
                customName: "phone_number",
                type: "string",
              },
              {
                name: "properties.jobtitle",
                customName: "job_title",
                type: "string",
              },
              { name: "id", customName: "hubspot_contact_id", type: "string" },
            ],
          },
        },
      ),
      n(
        "4",
        "condition",
        "Is this a first-time customer (no prior deals)?",
        760,
        {
          conditionDetails: {
            conditions: [
              { field: "deal_amount", operator: "greaterThan", value: "0" },
            ],
          },
        },
      ),
      emailNode(
        "5",
        "Email: Welcome and onboarding kickoff with setup guide",
        1000,
        "{contact_email}",
        "{welcome_subject}",
        "Hi {contact_name},\n\nWelcome to the family! We're excited to help {company_name} get the most out of {product_purchased}.\n\nHere's your personalized onboarding plan:\n\n{onboarding_plan}\n\nYour dedicated Customer Success Manager will reach out within 24 hours to schedule a kickoff call.\n\nIn the meantime, here are some resources to get started:\n- Quick Start Guide: https://docs.example.com/quickstart\n- Video Walkthrough: https://learn.example.com/onboarding\n- Help Center: https://help.example.com\n\nWe're here to make your transition seamless.\n\nBest,\nCustomer Success Team",
      ),
      n("6", "delayAction", "Wait 5 days for initial setup completion", 1240, {
        delayDetails: { duration: 5, unit: "days" },
      }),
      n(
        "7",
        "condition",
        "Customer has activated the product (lifecycle = customer)?",
        1480,
        {
          conditionDetails: {
            conditions: [
              { field: "first_name", operator: "notEquals", value: "" },
            ],
          },
        },
      ),
      emailNode(
        "8",
        "Email: Onboarding check-in with advanced tips and CSM meeting link",
        1720,
        "{contact_email}",
        "How's your setup going, {first_name}? Let's make sure you're on track",
        "Hi {first_name},\n\nIt's been 5 days since you started with {product_purchased} — how's everything going?\n\nHere are some advanced tips to accelerate your setup:\n\n1. Connect your existing tools via our integrations marketplace\n2. Invite your team members to collaborate\n3. Set up your first automated workflow using our templates\n\nIf you'd like personalized help, book a 30-minute session with your CSM:\nhttps://calendar.example.com/csm-onboarding\n\nWe want to make sure you're getting value from day one.\n\nCheers,\nCustomer Success Team",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
    summary:
      "Onboards new customers after a HubSpot deal closes won. Extracts deal details via AI to build a tailored onboarding plan, fetches the contact profile from HubSpot CRM, sends a welcome email with a personalized setup guide and resource links, waits 5 days for initial setup, checks activation status, and sends a follow-up check-in email with advanced tips and a CSM booking link to ensure time-to-value.",
  },

  // ─── 143. Customer Health Alert (Pattern L) ──────────────────────────

  "Customer Health Alert": {
    nodes: [
      trigger(
        "HubSpot webhook: Customer health score drops below threshold",
        "webhook_app",
        {
          source: "hubspot",
          event: "contact.propertyChange",
          howItStarts:
            "Fires when a HubSpot contact's custom health_score property drops below the warning threshold. The webhook payload contains the contact ID, previous score, new score, and associated company details.",
          setup:
            "In HubSpot, create a custom contact property 'health_score' (number). Set up a workflow trigger that fires when health_score changes and the new value is below 40. Subscribe to the contact.propertyChange webhook.",
          inputFields: [],
        },
      ),
      n(
        "2",
        "hitApi",
        "HubSpot: Fetch full account context — usage, tenure, deal history",
        280,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.hubapi.com/crm/v3/contacts/{contact_id}?properties=email,firstname,lastname,company,health_score,lifecyclestage,hs_object_id,total_revenue,num_associated_deals,last_activity_date",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              {
                name: "properties.email",
                customName: "contact_email",
                type: "string",
              },
              {
                name: "properties.firstname",
                customName: "first_name",
                type: "string",
              },
              {
                name: "properties.company",
                customName: "company_name",
                type: "string",
              },
              {
                name: "properties.health_score",
                customName: "health_score",
                type: "string",
              },
              {
                name: "properties.total_revenue",
                customName: "total_revenue",
                type: "string",
              },
              {
                name: "properties.last_activity_date",
                customName: "last_activity",
                type: "string",
              },
            ],
          },
        },
      ),
      aiNode(
        "3",
        "OpenAI: Analyze health drop signal and recommend intervention",
        520,
        "You are a customer success analyst. Given a customer's health score drop and account context, analyze the likely cause and recommend an intervention. Return: risk_level (critical/warning/watch), likely_cause (string), recommended_action (string), csm_talking_points (array of 3 points), and escalation_needed (boolean).",
        "Customer health score dropped. Contact: {first_name} at {company_name}. Health score: {health_score}/100. Total revenue: ${total_revenue}. Last activity: {last_activity}. Analyze the risk and recommend an intervention strategy.",
        [
          { field: "risk_level", as: "risk_level", type: "string" },
          { field: "likely_cause", as: "likely_cause", type: "string" },
          {
            field: "recommended_action",
            as: "recommended_action",
            type: "string",
          },
          {
            field: "csm_talking_points",
            as: "csm_talking_points",
            type: "object",
          },
          {
            field: "escalation_needed",
            as: "escalation_needed",
            type: "boolean",
          },
        ],
      ),
      n("4", "condition", "Risk level is critical (health < 20)?", 760, {
        conditionDetails: {
          conditions: [
            { field: "risk_level", operator: "equals", value: "critical" },
          ],
        },
      }),
      slackNode(
        "5",
        "Slack: Urgent CSM alert — critical churn risk with intervention plan",
        1000,
        "🚨 *CRITICAL HEALTH ALERT*\n*Customer:* {first_name} at {company_name}\n*Health Score:* {health_score}/100 | *Revenue:* ${total_revenue}\n*Risk Level:* {risk_level}\n*Likely Cause:* {likely_cause}\n*Recommended Action:* {recommended_action}\n*Talking Points:* {csm_talking_points}\n⚡ Immediate intervention required — schedule save call within 24 hours.",
      ),
      n(
        "6",
        "delayAction",
        "Wait 5 days for CSM intervention to take effect",
        1240,
        {
          delayDetails: { duration: 5, unit: "days" },
        },
      ),
      n(
        "7",
        "condition",
        "Health score still below 40 after intervention?",
        1480,
        {
          conditionDetails: {
            conditions: [
              { field: "health_score", operator: "lessThan", value: "40" },
            ],
          },
        },
      ),
      emailNode(
        "8",
        "Email: Executive escalation — at-risk account needs leadership attention",
        1720,
        "cs-leadership@company.com",
        "ESCALATION: {company_name} health score critical — intervention needed",
        "CS Leadership Team,\n\nThis is an automated escalation for a critical at-risk account.\n\n*Account:* {company_name}\n*Contact:* {first_name} ({contact_email})\n*Health Score:* {health_score}/100\n*Total Revenue:* ${total_revenue}\n*Risk Level:* {risk_level}\n*Likely Cause:* {likely_cause}\n\nThe initial CSM intervention was triggered 5 days ago, but the health score has not improved.\n\n*Recommended Next Steps:*\n{recommended_action}\n\nPlease review and assign executive sponsorship for this save play.\n\n— Customer Success Automation",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
    summary:
      "Monitors HubSpot customer health scores and triggers churn-prevention interventions when scores drop below threshold. Fetches full account context, uses AI to analyze the likely cause and recommend a save play, sends an urgent Slack alert with talking points for critical-risk accounts, waits 5 days for the CSM intervention, re-checks the health score, and escalates to CS leadership via email if the score has not recovered.",
  },

  // ─── 144. Renewal Reminder Sequence (Pattern F) ──────────────────────

  "Renewal Reminder Sequence": {
    nodes: [
      trigger(
        "Schedule: Daily check for contracts expiring in 60 days",
        "schedule",
        {
          schedule: "0 8 * * *",
          howItStarts:
            "Runs daily at 8 AM to identify HubSpot deals with renewal dates within the next 60 days. Fetches contracts approaching expiration and initiates the reminder sequence for each.",
          setup:
            "Create a HubSpot custom deal property 'renewal_date' (date). This workflow runs on a daily schedule and queries HubSpot for deals with renewal_date within the next 60 days.",
          inputFields: [],
        },
      ),
      n(
        "2",
        "hitApi",
        "Stripe: Check subscription payment status and billing health",
        280,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.stripe.com/v1/subscriptions/{subscription_id}",
            headers: [
              { key: "Authorization", value: "Bearer {stripe_secret_key}" },
              {
                key: "Content-Type",
                value: "application/x-www-form-urlencoded",
              },
            ],
            body: [],
            variables: [
              {
                name: "status",
                customName: "subscription_status",
                type: "string",
              },
              {
                name: "current_period_end",
                customName: "period_end",
                type: "number",
              },
              {
                name: "plan.amount",
                customName: "plan_amount",
                type: "number",
              },
              {
                name: "plan.nickname",
                customName: "plan_name",
                type: "string",
              },
              {
                name: "customer",
                customName: "stripe_customer_id",
                type: "string",
              },
            ],
          },
        },
      ),
      aiNode(
        "3",
        "OpenAI: Analyze renewal risk and craft personalized value recap",
        520,
        "You are a renewal strategy specialist. Given subscription details and customer context, assess renewal_risk (low/medium/high), generate a value_recap summarizing the customer's achievements and usage highlights, a personalized_subject for the renewal email, and renewal_incentive recommendation if risk is medium or high.",
        "Subscription: {plan_name} at ${plan_amount}/month. Status: {subscription_status}. Period ends: {period_end}. Customer: {contact_email} at {company_name}. Assess renewal risk and create a compelling value recap.",
        [
          { field: "renewal_risk", as: "renewal_risk", type: "string" },
          { field: "value_recap", as: "value_recap", type: "string" },
          {
            field: "personalized_subject",
            as: "personalized_subject",
            type: "string",
          },
          {
            field: "renewal_incentive",
            as: "renewal_incentive",
            type: "string",
          },
        ],
      ),
      n("4", "condition", "Payment at risk (failed or past due)?", 760, {
        conditionDetails: {
          conditions: [
            {
              field: "subscription_status",
              operator: "equals",
              value: "past_due",
            },
          ],
        },
      }),
      emailNode(
        "5",
        "Email: 60-day renewal reminder with value recap and early-bird incentive",
        1000,
        "{contact_email}",
        "{personalized_subject}",
        "Hi {contact_name},\n\nYour {plan_name} subscription renews in 60 days, and we wanted to share a quick recap of the value you've gotten:\n\n{value_recap}\n\n{renewal_incentive}\n\nTo ensure uninterrupted service, please confirm your renewal preference:\n- Renew current plan: https://billing.example.com/renew/{subscription_id}\n- Upgrade your plan: https://billing.example.com/upgrade/{subscription_id}\n- Talk to your CSM: https://calendar.example.com/csm-renewal\n\nWe're excited to continue our partnership with {company_name}.\n\nBest,\nCustomer Success Team",
      ),
      n(
        "6",
        "delayAction",
        "Wait 30 days — send second reminder at 30 days out",
        1240,
        {
          delayDetails: { duration: 30, unit: "days" },
        },
      ),
      n("7", "condition", "Subscription still not renewed?", 1480, {
        conditionDetails: {
          conditions: [
            {
              field: "subscription_status",
              operator: "notEquals",
              value: "active",
            },
          ],
        },
      }),
      emailNode(
        "8",
        "Email: 30-day urgent renewal with stronger incentive and CSM outreach",
        1720,
        "{contact_email}",
        "Your {plan_name} renewal is in 30 days — let's secure your spot",
        "Hi {contact_name},\n\nJust a heads up — your {plan_name} subscription renews in 30 days.\n\nWe know renewals can slip through the cracks, so here's what you need to know:\n\n✅ Your current plan: {plan_name} (${plan_amount}/month)\n✅ Renewal date: {period_end}\n✅ {renewal_incentive}\n\nIf you have any questions about your plan or want to explore upgrades, your CSM is standing by:\nhttps://calendar.example.com/csm-renewal\n\nDon't let your access lapse — renew today:\nhttps://billing.example.com/renew/{subscription_id}\n\nThank you for being a valued customer.\n\nBest,\nCustomer Success Team",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
    summary:
      "Proactively reminds customers about upcoming subscription renewals at 60 and 30 days before expiration. Checks Stripe for subscription payment health, uses AI to assess renewal risk and generate a personalized value recap, sends a 60-day early reminder with an incentive, waits 30 days, re-checks renewal status, and sends an urgent 30-day reminder with stronger messaging and a direct CSM booking link for at-risk accounts.",
  },

  // ─── 145. Cross-Sell by Product Owned (Pattern L) ────────────────────

  "Cross-Sell by Product Owned": {
    nodes: [
      trigger(
        "HubSpot webhook: Customer reaches product usage milestone",
        "webhook_app",
        {
          source: "hubspot",
          event: "contact.propertyChange",
          howItStarts:
            "Fires when a HubSpot contact's product_usage_score property exceeds the cross-sell threshold, indicating they are successfully using their current product and may be ready for complementary offerings.",
          setup:
            "In HubSpot, create a custom contact property 'product_usage_score' (number) and 'products_owned' (multi-select). Trigger this workflow when product_usage_score exceeds 70.",
          inputFields: [],
        },
      ),
      n(
        "2",
        "hitApi",
        "HubSpot: Fetch customer profile with products owned and account tier",
        280,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.hubapi.com/crm/v3/contacts/{contact_id}?properties=email,firstname,lastname,company,products_owned,product_usage_score,total_revenue,lifecyclestage,hs_lead_status",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              {
                name: "properties.email",
                customName: "contact_email",
                type: "string",
              },
              {
                name: "properties.firstname",
                customName: "first_name",
                type: "string",
              },
              {
                name: "properties.company",
                customName: "company_name",
                type: "string",
              },
              {
                name: "properties.products_owned",
                customName: "products_owned",
                type: "string",
              },
              {
                name: "properties.product_usage_score",
                customName: "usage_score",
                type: "string",
              },
              {
                name: "properties.total_revenue",
                customName: "total_revenue",
                type: "string",
              },
            ],
          },
        },
      ),
      aiNode(
        "3",
        "OpenAI: Analyze products owned and recommend best cross-sell match",
        520,
        "You are a product expansion strategist. Given the customer's current products and usage score, recommend the best cross-sell product. Return: recommended_product (string), cross_sell_reason (why this product complements what they own), personalized_pitch (2-3 sentences), estimated_value (string), and expansion_priority (high/medium/low).",
        "Customer: {first_name} at {company_name}. Products owned: {products_owned}. Usage score: {usage_score}/100. Total revenue: ${total_revenue}. Recommend the best complementary product and craft a personalized pitch.",
        [
          {
            field: "recommended_product",
            as: "recommended_product",
            type: "string",
          },
          {
            field: "cross_sell_reason",
            as: "cross_sell_reason",
            type: "string",
          },
          {
            field: "personalized_pitch",
            as: "personalized_pitch",
            type: "string",
          },
          { field: "estimated_value", as: "estimated_value", type: "string" },
          {
            field: "expansion_priority",
            as: "expansion_priority",
            type: "string",
          },
        ],
      ),
      n(
        "4",
        "condition",
        "Expansion priority is high (usage > 85 and revenue > $10K)?",
        760,
        {
          conditionDetails: {
            conditions: [
              {
                field: "expansion_priority",
                operator: "equals",
                value: "high",
              },
            ],
          },
        },
      ),
      emailNode(
        "5",
        "Email: Personalized cross-sell recommendation with product value prop",
        1000,
        "{contact_email}",
        "{first_name}, based on your success with {products_owned} — meet {recommended_product}",
        "Hi {first_name},\n\nGreat news — your team at {company_name} has been getting strong results with {products_owned} (usage score: {usage_score}/100).\n\n{personalized_pitch}\n\nWhy {recommended_product} is a perfect fit:\n{cross_sell_reason}\n\nEstimated additional value: {estimated_value}\n\nSee how it works: https://product.example.com/{recommended_product}/demo\nTalk to your account manager: https://calendar.example.com/expansion\n\nBest,\nAccount Team",
      ),
      n(
        "6",
        "delayAction",
        "Wait 7 days for customer to evaluate the recommendation",
        1240,
        {
          delayDetails: { duration: 7, unit: "days" },
        },
      ),
      n("7", "condition", "Customer has not started a trial or demo?", 1480, {
        conditionDetails: {
          conditions: [
            { field: "usage_score", operator: "greaterThan", value: "70" },
          ],
        },
      }),
      slackNode(
        "8",
        "Slack: Alert account manager — warm cross-sell opportunity needs follow-up",
        1720,
        "💡 *Cross-Sell Opportunity — Follow-Up Needed*\n*Customer:* {first_name} at {company_name}\n*Current Products:* {products_owned}\n*Recommended:* {recommended_product}\n*Priority:* {expansion_priority} | *Est. Value:* {estimated_value}\n*Pitch Sent:* 7 days ago, no engagement yet.\nRecommend personal outreach to discuss {recommended_product} fit.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
    summary:
      "Identifies cross-sell opportunities based on product usage milestones in HubSpot. Fetches the customer's product portfolio and usage data, uses AI to recommend the best complementary product with a personalized pitch, sends a cross-sell email for high-priority expansion opportunities, waits 7 days for evaluation, checks engagement, and alerts the account manager via Slack if the customer hasn't taken action to prompt personal follow-up.",
  },

  // ─── 146. Product Usage Alert to Sales (Pattern L) ───────────────────

  "Product Usage Alert to Sales": {
    nodes: [
      trigger(
        "HubSpot webhook: Product usage exceeds plan limit threshold",
        "webhook_app",
        {
          source: "hubspot",
          event: "contact.propertyChange",
          howItStarts:
            "Fires when a HubSpot contact's usage_percentage property exceeds 80% of their plan limit, indicating potential need for an upgrade or expansion. The webhook contains the contact ID and updated usage metrics.",
          setup:
            "In HubSpot, create custom contact properties 'usage_percentage' (number) and 'current_plan' (string). Trigger when usage_percentage exceeds 80. Sync usage data from your product via API or integration.",
          inputFields: [],
        },
      ),
      n(
        "2",
        "hitApi",
        "HubSpot: Fetch account context — plan, usage, revenue, and owner",
        280,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.hubapi.com/crm/v3/contacts/{contact_id}?properties=email,firstname,lastname,company,current_plan,usage_percentage,total_revenue,hubspot_owner_id,lifecyclestage",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              {
                name: "properties.email",
                customName: "contact_email",
                type: "string",
              },
              {
                name: "properties.firstname",
                customName: "first_name",
                type: "string",
              },
              {
                name: "properties.company",
                customName: "company_name",
                type: "string",
              },
              {
                name: "properties.current_plan",
                customName: "current_plan",
                type: "string",
              },
              {
                name: "properties.usage_percentage",
                customName: "usage_pct",
                type: "string",
              },
              {
                name: "properties.total_revenue",
                customName: "total_revenue",
                type: "string",
              },
              {
                name: "properties.hubspot_owner_id",
                customName: "owner_id",
                type: "string",
              },
            ],
          },
        },
      ),
      aiNode(
        "3",
        "OpenAI: Analyze usage pattern and recommend expansion or upgrade path",
        520,
        "You are a product-led growth analyst. Given usage data and account context, determine: signal_type (expansion_ready/approaching_limit/overage_risk), recommended_upgrade (string), upgrade_value_prop (why they should upgrade), urgency (high/medium/low), and sales_talking_points (array of 3 points for the AE).",
        "Customer: {first_name} at {company_name}. Plan: {current_plan}. Usage: {usage_pct}% of limit. Revenue: ${total_revenue}. Analyze this usage signal and recommend an expansion path with sales talking points.",
        [
          { field: "signal_type", as: "signal_type", type: "string" },
          {
            field: "recommended_upgrade",
            as: "recommended_upgrade",
            type: "string",
          },
          {
            field: "upgrade_value_prop",
            as: "upgrade_value_prop",
            type: "string",
          },
          { field: "urgency", as: "urgency", type: "string" },
          {
            field: "sales_talking_points",
            as: "sales_talking_points",
            type: "object",
          },
        ],
      ),
      n("4", "condition", "Usage above 90% (overage risk — urgent)?", 760, {
        conditionDetails: {
          conditions: [
            { field: "usage_pct", operator: "greaterThan", value: "90" },
          ],
        },
      }),
      slackNode(
        "5",
        "Slack: Sales alert — expansion opportunity with upgrade recommendation",
        1000,
        "📈 *Product Usage Alert — Expansion Opportunity*\n*Customer:* {first_name} at {company_name}\n*Plan:* {current_plan} | *Usage:* {usage_pct}%\n*Signal:* {signal_type} | *Urgency:* {urgency}\n*Revenue:* ${total_revenue}\n*Recommended Upgrade:* {recommended_upgrade}\n*Value Prop:* {upgrade_value_prop}\n*Talking Points:* {sales_talking_points}\nOwner: {owner_id} — reach out within 48 hours.",
      ),
      n(
        "6",
        "delayAction",
        "Wait 5 days for sales outreach and customer response",
        1240,
        {
          delayDetails: { duration: 5, unit: "days" },
        },
      ),
      n(
        "7",
        "condition",
        "Usage still at or above 80% (no upgrade yet)?",
        1480,
        {
          conditionDetails: {
            conditions: [
              {
                field: "usage_pct",
                operator: "greaterThanOrEqual",
                value: "80",
              },
            ],
          },
        },
      ),
      emailNode(
        "8",
        "Email: Customer notification — approaching plan limit with upgrade options",
        1720,
        "{contact_email}",
        "{first_name}, you're getting close to your {current_plan} plan limit",
        "Hi {first_name},\n\nGreat news — your team at {company_name} has been making excellent use of your {current_plan} plan! You're currently at {usage_pct}% of your limit.\n\n{upgrade_value_prop}\n\nTo avoid any disruption, here are your options:\n\n1. Upgrade to {recommended_upgrade}: https://billing.example.com/upgrade\n2. Talk to your account manager: https://calendar.example.com/expansion\n3. View plan comparison: https://pricing.example.com/compare\n\nWe're here to help you scale smoothly.\n\nBest,\nAccount Team",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
    summary:
      "Alerts sales when product usage approaches plan limits, creating expansion opportunities. Fetches account context from HubSpot, uses AI to analyze the usage signal and recommend an upgrade path with sales talking points, sends an urgent Slack alert to the account owner for high-usage accounts (90%+), waits 5 days for sales outreach, re-checks usage, and proactively emails the customer with upgrade options if no action has been taken.",
  },

  // ─── 147. Deal Stage Change Notification (Pattern K) ─────────────────

  "Deal Stage Change Notification": {
    nodes: [
      trigger(
        "HubSpot webhook: Deal stage changes in pipeline",
        "webhook_app",
        {
          source: "hubspot",
          event: "deal.propertyChange",
          howItStarts:
            "Fires whenever a HubSpot deal's stage changes (e.g., from Qualified to Proposal Sent, or from Negotiation to Closed-Won). The webhook payload contains the deal ID, previous stage, new stage, deal amount, and associated contact.",
          setup:
            "In HubSpot, subscribe to deal.propertyChange webhook events filtered to the 'dealstage' property. Ensure all pipeline stages are properly configured.",
          inputFields: [],
        },
      ),
      n(
        "2",
        "hitApi",
        "HubSpot: Fetch deal details with amount, owner, and associations",
        280,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.hubapi.com/crm/v3/objects/deals/{deal_id}?properties=dealname,dealstage,amount,hubspot_owner_id,closedate,pipeline,hs_deal_stage_probability,associated_contact_email",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              {
                name: "properties.dealname",
                customName: "deal_name",
                type: "string",
              },
              {
                name: "properties.dealstage",
                customName: "deal_stage",
                type: "string",
              },
              {
                name: "properties.amount",
                customName: "deal_amount",
                type: "string",
              },
              {
                name: "properties.hubspot_owner_id",
                customName: "deal_owner",
                type: "string",
              },
              {
                name: "properties.closedate",
                customName: "close_date",
                type: "string",
              },
              {
                name: "properties.pipeline",
                customName: "pipeline",
                type: "string",
              },
              {
                name: "properties.hs_deal_stage_probability",
                customName: "win_probability",
                type: "string",
              },
            ],
          },
        },
      ),
      n(
        "3",
        "runCode",
        "Compute stage progression metrics and SLA requirements",
        520,
        {
          runCodeDetails: {
            code: `const stage = input.deal_stage || "";
const amount = parseInt(input.deal_amount) || 0;
const probability = parseInt(input.win_probability) || 0;
const stageConfig = {
  qualifiedtobuy: { label: "Qualified to Buy", sla_days: 7, next_action: "Schedule discovery call" },
  presentationscheduled: { label: "Presentation Scheduled", sla_days: 5, next_action: "Deliver demo/presentation" },
  decisionmakerboughtin: { label: "Decision Maker Bought In", sla_days: 10, next_action: "Send proposal" },
  contractsent: { label: "Contract Sent", sla_days: 14, next_action: "Follow up on contract review" },
  closedwon: { label: "Closed Won", sla_days: 1, next_action: "Hand off to CS team" },
  closedlost: { label: "Closed Lost", sla_days: 1, next_action: "Log loss reason and recycle" },
};
const config = stageConfig[stage] || { label: stage, sla_days: 7, next_action: "Review and advance" };
const is_high_value = amount >= 50000;
const alert_level = (stage === "closedwon" || stage === "closedlost") ? "outcome" : is_high_value ? "high" : "standard";
return { stage_label: config.label, sla_days: config.sla_days, next_action: config.next_action, is_high_value, alert_level };`,
          },
        },
      ),
      n(
        "4",
        "condition",
        "Is this a high-value deal ($50K+) or outcome stage?",
        760,
        {
          conditionDetails: {
            conditions: [
              { field: "is_high_value", operator: "equals", value: "true" },
            ],
          },
        },
      ),
      slackNode(
        "5",
        "Slack: Team notification — deal stage change with context and next action",
        1000,
        "🔄 *Deal Stage Change*\n*Deal:* {deal_name}\n*New Stage:* {stage_label} | *Amount:* ${deal_amount}\n*Win Probability:* {win_probability}% | *Alert Level:* {alert_level}\n*Owner:* {deal_owner} | *Pipeline:* {pipeline}\n*Next Action:* {next_action} (SLA: {sla_days} days)\n*Close Date:* {close_date}",
      ),
      n("6", "delayAction", "Wait for SLA period to check compliance", 1240, {
        delayDetails: { duration: 3, unit: "days" },
      }),
      n(
        "7",
        "hitApi",
        "HubSpot: Update deal with stage progression timestamp and SLA status",
        1480,
        {
          hitApiDetails: {
            method: "PATCH",
            url: "https://api.hubapi.com/crm/v3/objects/deals/{deal_id}",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "properties",
                value:
                  '{"stage_change_notified":"true","last_stage_change_date":"{new Date().toISOString()}"}',
              },
            ],
            variables: [
              { name: "id", customName: "updated_deal_id", type: "string" },
            ],
          },
        },
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log deal stage transition for pipeline analytics",
        1720,
        "Deal_Stage_Log",
        '[["{deal_name}","{deal_stage}","{stage_label}","{deal_amount}","{win_probability}","{deal_owner}","{alert_level}","{new Date().toISOString()}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Notifies the sales team whenever a HubSpot deal changes stage and enforces SLA compliance. Fetches deal details including amount and win probability, computes stage-specific SLA requirements and next actions, sends a Slack notification with deal context (escalated for high-value $50K+ deals), waits the SLA period, updates the deal record with progression timestamps, and logs every stage transition to Google Sheets for pipeline velocity analytics.",
  },

  // ─── 148. Stale Deal Nudge (Pattern G) ────────────────────────────────

  "Stale Deal Nudge": {
    nodes: [
      trigger(
        "Schedule: Daily scan for deals with no activity in 14+ days",
        "schedule",
        {
          schedule: "0 9 * * 1-5",
          howItStarts:
            "Runs every weekday at 9 AM to identify HubSpot deals that have had no activity (no notes, emails, calls, or stage changes) for 14 or more days, indicating they may be stalling.",
          setup:
            "Ensure HubSpot deal properties include 'notes_last_updated' or leverage the last activity date. This workflow queries the HubSpot API for deals with last_activity > 14 days ago.",
          inputFields: [],
        },
      ),
      n(
        "2",
        "hitApi",
        "HubSpot: Search for stale deals with last activity > 14 days ago",
        280,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.hubapi.com/crm/v3/objects/deals/search",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "filterGroups",
                value:
                  '[{"filters":[{"propertyName":"dealstage","operator":"NOT_IN","values":["closedwon","closedlost"]},{"propertyName":"notes_last_updated","operator":"LT","value":"{fourteen_days_ago}"}]}]',
              },
              {
                key: "properties",
                value:
                  '["dealname","dealstage","amount","hubspot_owner_id","closedate","notes_last_updated"]',
              },
              { key: "limit", value: "50" },
            ],
            variables: [
              {
                name: "results[0].properties.dealname",
                customName: "deal_name",
                type: "string",
              },
              {
                name: "results[0].properties.dealstage",
                customName: "deal_stage",
                type: "string",
              },
              {
                name: "results[0].properties.amount",
                customName: "deal_amount",
                type: "string",
              },
              {
                name: "results[0].properties.hubspot_owner_id",
                customName: "deal_owner",
                type: "string",
              },
              {
                name: "results[0].properties.closedate",
                customName: "close_date",
                type: "string",
              },
              {
                name: "results[0].properties.notes_last_updated",
                customName: "last_activity",
                type: "string",
              },
              { name: "total", customName: "stale_deal_count", type: "number" },
            ],
          },
        },
      ),
      aiNode(
        "3",
        "OpenAI: Analyze stale deal pattern and suggest re-engagement tactics",
        520,
        "You are a sales pipeline analyst. Given a stale deal (no activity for 14+ days), analyze the likely reason for stalling and suggest: stall_reason (string), re_engagement_tactic (specific action the AE should take), email_template_suggestion (a brief outreach message), and priority (high/medium/low) based on deal size and stage.",
        "Stale deal: {deal_name}. Stage: {deal_stage}. Amount: ${deal_amount}. Owner: {deal_owner}. Last activity: {last_activity}. Close date: {close_date}. Diagnose why this deal might be stalling and suggest a re-engagement approach.",
        [
          { field: "stall_reason", as: "stall_reason", type: "string" },
          {
            field: "re_engagement_tactic",
            as: "re_engagement_tactic",
            type: "string",
          },
          {
            field: "email_template_suggestion",
            as: "email_template_suggestion",
            type: "string",
          },
          { field: "priority", as: "priority", type: "string" },
        ],
      ),
      n("4", "condition", "Is this a high-value deal ($25K+)?", 760, {
        conditionDetails: {
          conditions: [
            {
              field: "deal_amount",
              operator: "greaterThanOrEqual",
              value: "25000",
            },
          ],
        },
      }),
      emailNode(
        "5",
        "Email: AE nudge — stale deal alert with re-engagement suggestions",
        1000,
        "{deal_owner}",
        "⏰ Stale Deal Alert: {deal_name} — no activity for 14+ days",
        'Hi,\n\nYour deal "{deal_name}" (${deal_amount}) has been sitting in the {deal_stage} stage with no activity for over 14 days.\n\n*Why it might be stalling:*\n{stall_reason}\n\n*Suggested re-engagement tactic:*\n{re_engagement_tactic}\n\n*Draft outreach message:*\n{email_template_suggestion}\n\nPlease update this deal by end of day — either advance it, add a note, or flag for pipeline review.\n\nView deal: https://app.hubspot.com/contacts/deals/{deal_id}\n\n— Pipeline Automation',
      ),
      n("6", "delayAction", "Wait 7 days for AE to take action", 1240, {
        delayDetails: { duration: 7, unit: "days" },
      }),
      n("7", "condition", "Deal still stale after 21 total days?", 1480, {
        conditionDetails: {
          conditions: [
            { field: "stale_deal_count", operator: "greaterThan", value: "0" },
          ],
        },
      }),
      slackNode(
        "8",
        "Slack: Manager escalation — deal stale for 21+ days, needs review",
        1720,
        "⚠️ *Manager Escalation — Stale Deal (21+ days)*\n*Deal:* {deal_name} | *Stage:* {deal_stage}\n*Amount:* ${deal_amount} | *Priority:* {priority}\n*Owner:* {deal_owner}\n*Stall Reason:* {stall_reason}\n*Last Activity:* {last_activity}\nThis deal has been inactive for 21+ days despite an AE nudge 7 days ago. Recommend pipeline review.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
    summary:
      "Identifies and nudges stale HubSpot deals that have had no activity for 14+ days. Searches for inactive open deals, uses AI to diagnose the likely stall reason and suggest re-engagement tactics, emails the deal owner with actionable advice and a draft outreach message, waits 7 more days, re-checks activity, and escalates to the sales manager via Slack if the deal remains stale after 21 total days of inactivity.",
  },

  // ─── 149. Proposal Sent Follow-Up (Pattern I) ────────────────────────

  "Proposal Sent Follow-Up": {
    nodes: [
      trigger(
        "HubSpot webhook: Deal stage changes to Proposal Sent",
        "webhook_app",
        {
          source: "hubspot",
          event: "deal.propertyChange",
          howItStarts:
            "Fires when a HubSpot deal moves to the 'Proposal Sent' or 'Contract Sent' stage. The webhook payload contains the deal ID, deal name, amount, associated contact, and the timestamp when the proposal was sent.",
          setup:
            "In HubSpot, subscribe to deal.propertyChange events filtered to dealstage = 'contractsent' or a custom 'proposalsent' stage. Map webhook URL to this workflow.",
          inputFields: [],
        },
      ),
      aiNode(
        "2",
        "OpenAI: Extract proposal details and generate follow-up strategy",
        280,
        "You are a sales follow-up strategist. Given a deal that just entered the proposal stage, extract: contact_email, contact_name, company_name, deal_amount, proposal_date, and generate a follow_up_subject, check_in_message (warm, non-pushy), objection_handlers (array of 3 common objections with responses), and next_step_suggestion.",
        "Deal moved to proposal sent. Deal data: {webhook_payload}. Create a follow-up strategy with a warm check-in message and prepare objection handlers for common pushback scenarios.",
        [
          { field: "contact_email", as: "contact_email", type: "string" },
          { field: "contact_name", as: "contact_name", type: "string" },
          { field: "company_name", as: "company_name", type: "string" },
          { field: "deal_amount", as: "deal_amount", type: "string" },
          {
            field: "follow_up_subject",
            as: "follow_up_subject",
            type: "string",
          },
          { field: "check_in_message", as: "check_in_message", type: "string" },
          {
            field: "next_step_suggestion",
            as: "next_step_suggestion",
            type: "string",
          },
        ],
      ),
      n(
        "3",
        "hitApi",
        "HubSpot: Fetch contact lifecycle stage and engagement history",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.hubapi.com/crm/v3/contacts/{contact_email}?idProperty=email&properties=email,firstname,lifecyclestage,num_associated_deals,recent_deal_close_date,hs_email_last_open_date",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              {
                name: "properties.lifecyclestage",
                customName: "lifecycle_stage",
                type: "string",
              },
              {
                name: "properties.num_associated_deals",
                customName: "total_deals",
                type: "string",
              },
              {
                name: "properties.hs_email_last_open_date",
                customName: "last_email_open",
                type: "string",
              },
            ],
          },
        },
      ),
      n(
        "4",
        "condition",
        "Contact has opened recent emails (engaged prospect)?",
        760,
        {
          conditionDetails: {
            conditions: [
              { field: "last_email_open", operator: "notEquals", value: "" },
            ],
          },
        },
      ),
      emailNode(
        "5",
        "Email: Warm check-in 3 days after proposal with value reinforcement",
        1000,
        "{contact_email}",
        "{follow_up_subject}",
        "Hi {contact_name},\n\n{check_in_message}\n\nI wanted to make sure the proposal addressed everything {company_name} needs. A few quick highlights:\n\n- Tailored to your team's specific requirements\n- ROI projections based on your current metrics\n- Flexible implementation timeline\n\nIf you have any questions or would like to walk through any section together, I'm happy to schedule a quick call:\nhttps://calendar.example.com/proposal-review\n\n{next_step_suggestion}\n\nLooking forward to hearing your thoughts.\n\nBest regards",
      ),
      n(
        "6",
        "delayAction",
        "Wait 4 days for prospect to review and respond",
        1240,
        {
          delayDetails: { duration: 4, unit: "days" },
        },
      ),
      n(
        "7",
        "condition",
        "Prospect has not responded or advanced the deal?",
        1480,
        {
          conditionDetails: {
            conditions: [
              {
                field: "lifecycle_stage",
                operator: "notEquals",
                value: "customer",
              },
            ],
          },
        },
      ),
      emailNode(
        "8",
        "Email: Second follow-up — next steps prompt with urgency and case study",
        1720,
        "{contact_email}",
        "Quick follow-up on your {company_name} proposal — next steps?",
        "Hi {contact_name},\n\nJust circling back on the proposal I sent over for {company_name}. I know these decisions take time, so no rush — but I wanted to share a couple of things that might help:\n\n📊 A similar company in your space saw 3x ROI within the first 90 days — I can share the full case study if helpful.\n\n🗓️ If timing is the concern, we can discuss a phased rollout that fits your schedule.\n\nWhat would be most helpful as a next step?\n1. Walk through the proposal together\n2. Share the case study\n3. Discuss adjusted timeline/pricing\n\nJust reply with a number, and I'll take it from there.\n\nBest regards",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
    summary:
      "Automates follow-up after a sales proposal is sent through HubSpot. Extracts proposal details via AI and generates a warm follow-up strategy with objection handlers, checks the contact's email engagement history, sends a value-reinforcing check-in email 3 days post-proposal, waits 4 more days for response, verifies the deal hasn't advanced, and sends a second follow-up with a case study reference and clear next-step options to move the deal forward.",
  },

  // ─── 150. Quote Request Acknowledgment (Pattern K) ───────────────────

  "Quote Request Acknowledgment": {
    nodes: [
      trigger("HubSpot webhook: Quote request form submitted", "webhook_app", {
        source: "hubspot",
        event: "form.submission.created",
        howItStarts:
          "Fires when a prospect submits a quote request form in HubSpot. The webhook contains the form ID, submitted fields (product interest, quantity, company details), page URL, and contact information.",
        setup:
          "In HubSpot, create a dedicated quote request form. Subscribe to form.submission.created webhook events and filter by the specific form GUID for quote requests.",
        inputFields: [],
      }),
      n(
        "2",
        "hitApi",
        "HubSpot: Enrich contact with company size, industry, and deal history",
        280,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.hubapi.com/crm/v3/contacts/{contact_id}?properties=email,firstname,lastname,company,industry,numberofemployees,annualrevenue,lifecyclestage,num_associated_deals",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [],
            variables: [
              {
                name: "properties.email",
                customName: "contact_email",
                type: "string",
              },
              {
                name: "properties.firstname",
                customName: "first_name",
                type: "string",
              },
              {
                name: "properties.lastname",
                customName: "last_name",
                type: "string",
              },
              {
                name: "properties.company",
                customName: "company_name",
                type: "string",
              },
              {
                name: "properties.industry",
                customName: "industry",
                type: "string",
              },
              {
                name: "properties.numberofemployees",
                customName: "company_size",
                type: "string",
              },
              {
                name: "properties.annualrevenue",
                customName: "annual_revenue",
                type: "string",
              },
              {
                name: "properties.num_associated_deals",
                customName: "existing_deals",
                type: "string",
              },
            ],
          },
        },
      ),
      n(
        "3",
        "runCode",
        "Compute quote priority and route to appropriate sales rep",
        520,
        {
          runCodeDetails: {
            code: `const revenue = parseInt(input.annual_revenue) || 0;
const employees = parseInt(input.company_size) || 0;
const existingDeals = parseInt(input.existing_deals) || 0;
let priority = "standard";
let sla_hours = 24;
let assigned_team = "inside-sales@company.com";
if (revenue > 5000000 || employees > 500) { priority = "enterprise"; sla_hours = 4; assigned_team = "enterprise-sales@company.com"; }
else if (revenue > 500000 || employees > 50) { priority = "mid-market"; sla_hours = 8; assigned_team = "mid-market-sales@company.com"; }
const is_existing_customer = existingDeals > 0;
return { priority, sla_hours, assigned_team, is_existing_customer, routing_reason: priority + " tier based on $" + revenue + " revenue and " + employees + " employees" };`,
          },
        },
      ),
      n("4", "condition", "Is this an enterprise-tier quote request?", 760, {
        conditionDetails: {
          conditions: [
            { field: "priority", operator: "equals", value: "enterprise" },
          ],
        },
      }),
      emailNode(
        "5",
        "Email: Instant acknowledgment to prospect with expected timeline",
        1000,
        "{contact_email}",
        "We received your quote request, {first_name} — here's what happens next",
        "Hi {first_name},\n\nThank you for requesting a quote for {company_name}! We've received your request and here's what to expect:\n\n📋 *Your request has been assigned to our {priority} team*\n⏱️ *Expected response time:* Within {sla_hours} hours\n📧 *Your dedicated rep:* {assigned_team}\n\nIn the meantime, you might find these resources helpful:\n- Pricing overview: https://pricing.example.com\n- Product comparison: https://product.example.com/compare\n- Customer stories: https://customers.example.com\n\nIf you need anything urgent, reply to this email or call us directly.\n\nWe're looking forward to working with {company_name}!\n\nBest,\nSales Team",
      ),
      n(
        "6",
        "delayAction",
        "Wait for SLA window to check follow-up compliance",
        1240,
        {
          delayDetails: { duration: 4, unit: "hours" },
        },
      ),
      n(
        "7",
        "hitApi",
        "HubSpot: Create deal for quote request and assign to routed owner",
        1480,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.hubapi.com/crm/v3/objects/deals",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "properties",
                value:
                  '{"dealname":"Quote Request — {company_name}","dealstage":"qualifiedtobuy","pipeline":"default","hubspot_owner_id":"{assigned_team}","quote_priority":"{priority}","quote_sla_hours":"{sla_hours}"}',
              },
            ],
            variables: [
              { name: "id", customName: "new_deal_id", type: "string" },
            ],
          },
        },
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log quote request with routing and SLA for tracking",
        1720,
        "Quote_Request_Log",
        '[["{contact_email}","{company_name}","{industry}","{priority}","{assigned_team}","{sla_hours}","{is_existing_customer}","{new Date().toISOString()}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Acknowledges quote requests instantly and routes them to the right sales team based on company tier. Enriches the contact from HubSpot with company size and revenue data, computes quote priority and SLA (enterprise: 4 hours, mid-market: 8 hours, standard: 24 hours), sends an immediate confirmation email with expected response timeline, creates a HubSpot deal assigned to the appropriate rep, and logs the routing decision to Google Sheets for SLA compliance tracking.",
  },
};
